import { componentSchemaString } from "../decorators/type.js";
import "./types.js";
import { ByteArray } from "../utils/byteArray.js";
import { a as assignGlobalSingleton, s as setGlobalSingleton, g as getGlobalSingleton } from "../global-33d5eebf.js";
import { C as ComponentCategory } from "../enums-86f58524.js";
import "ajv";
import "../utils/clone.js";
import "bitecs";
import "../utils/typehelpers.js";
var DEPTHS = /* @__PURE__ */ ((DEPTHS2) => {
  DEPTHS2[DEPTHS2["NORUN"] = -1e7] = "NORUN";
  DEPTHS2[DEPTHS2["CORE"] = 0] = "CORE";
  DEPTHS2[DEPTHS2["KILLSTATS"] = 1] = "KILLSTATS";
  DEPTHS2[DEPTHS2["HEALTH"] = 1e3] = "HEALTH";
  DEPTHS2[DEPTHS2["PLAYER_MOVEMENT"] = 2e3] = "PLAYER_MOVEMENT";
  DEPTHS2[DEPTHS2["LOCOMOTION"] = 3e3] = "LOCOMOTION";
  DEPTHS2[DEPTHS2["COLLISION"] = 4e3] = "COLLISION";
  DEPTHS2[DEPTHS2["DAMAGE"] = 5e3] = "DAMAGE";
  DEPTHS2[DEPTHS2["ITEMS"] = 6e3] = "ITEMS";
  DEPTHS2[DEPTHS2["TRIGGERS"] = 7e3] = "TRIGGERS";
  DEPTHS2[DEPTHS2["ANIMATE"] = 8e3] = "ANIMATE";
  DEPTHS2[DEPTHS2["PREDRAW"] = 9e3] = "PREDRAW";
  DEPTHS2[DEPTHS2["DRAW"] = 1e4] = "DRAW";
  return DEPTHS2;
})(DEPTHS || {});
const getIndex = (type) => {
  let index;
  if (typeof type === "function") {
    index = componentIndexMap[type.__type];
  } else if (typeof type === "string") {
    index = componentIndexMap[type];
  } else {
    index = type;
  }
  return index;
};
assignGlobalSingleton("canRegisterComponents", () => true);
const canRegisterComponents = (set) => {
  if (set !== void 0) {
    setGlobalSingleton("canRegisterComponents", set);
  }
  return getGlobalSingleton("canRegisterComponents");
};
const componentList = assignGlobalSingleton("componentList", () => []);
const componentIndexMap = assignGlobalSingleton("componentIndexMap", () => ({}));
const componentsByCategory = assignGlobalSingleton(
  "componentsByCategory",
  () => ({})
);
const systems = assignGlobalSingleton("systems", () => []);
const uiComponents = assignGlobalSingleton("uiComponents", () => []);
const uiComponentsById = assignGlobalSingleton("uiComponentsById", () => ({}));
const pixiDrawComponents = assignGlobalSingleton("pixiDrawComponents", () => []);
const editorComponents = assignGlobalSingleton("editorComponents", () => ({}));
function _registerSchema(category, schema) {
  if (schema === void 0) {
    schema = category;
    category = ComponentCategory.NONE;
  }
  const type = componentSchemaString.get(schema);
  if (!type) {
    throw new Error("Schema not registered as a component" + schema);
  }
  if (componentIndexMap[type]) {
    return;
  }
  componentIndexMap[type] = componentList.length;
  componentsByCategory[category] = componentsByCategory[category] ?? [];
  componentsByCategory[category].push(componentList.length);
  componentList.push({
    category,
    type,
    schema,
    depth: -1
  });
  componentsByCategory[category].sort((a, b) => {
    const aDepth = componentList[a].depth;
    const bDepth = componentList[b].depth;
    if (aDepth === bDepth) {
      return a - b;
    } else {
      return aDepth - bDepth;
    }
  });
}
const registerSchema = _registerSchema;
const registerSystem = (system) => {
  if (!canRegisterComponents()) {
    throw new Error("Cannot register components after game has started");
  }
  systems.push(system);
};
const generateSystems = (gameModel) => {
  const modelSystems = {};
  systems.forEach((systemConstructor) => {
    const system = new systemConstructor(gameModel);
    const type = system.type;
    system.constructor.__type = type;
    if (!componentIndexMap[type]) {
      componentIndexMap[type] = componentList.length;
      const category = system.category ?? system.schema.__category ?? ComponentCategory.NONE;
      componentsByCategory[category] = componentsByCategory[category] ?? [];
      componentsByCategory[category].push(componentList.length);
      componentList.push({
        category,
        type,
        schema: system.schema,
        depth: system.depth ?? -1
      });
      const pixiDrawComponent = pixiDrawComponents.find((c) => c.type === type);
      if (pixiDrawComponent !== void 0) {
        pixiDrawComponent.id = componentIndexMap[type];
      }
      const drawComponent = uiComponents.find((c) => c.type === type);
      if (drawComponent !== void 0) {
        drawComponent.id = componentIndexMap[type];
      }
    }
    modelSystems[type] = system;
  });
  return modelSystems;
};
const generateRunList = (systems2) => {
  const runList = systems2.slice().filter((system) => (system.depth ?? -1) >= 0).sort((a, b) => {
    const diff = (a.depth ?? 0) - (b.depth ?? 0);
    if (diff === 0) {
      return a.type.localeCompare(b.type);
    } else {
      return diff;
    }
  });
  const coreRunList = runList.filter(
    (system) => (system.depth ?? 0) < 9e3
    /* PREDRAW */
  );
  const drawRunList = runList.filter(
    (system) => (system.depth ?? 0) >= 9e3
    /* PREDRAW */
  );
  const intraFrameKeys = runList.reduce((acc, system) => {
    if (system.intraFrame) {
      if (system.intraFrame > 60) {
        throw new Error(`Intraframe value for ${system.type} is too high`);
      }
      acc.push(system.intraFrame);
    }
    return acc;
  }, []);
  const finalRunList = {};
  finalRunList[0] = [coreRunList.filter((c) => !c.intraFrame).map((c) => c.type), drawRunList.map((c) => c.type)];
  if (intraFrameKeys.length !== 0) {
    for (let i = 1; i <= 60; i++) {
      const frame = i;
      finalRunList[frame] = [[], []];
      for (let j = 0; j < coreRunList.length; j++) {
        const system = coreRunList[j];
        if (!system.intraFrame || frame % system.intraFrame === 0) {
          finalRunList[frame][0].push(coreRunList[j].type);
        }
      }
      for (let j = 0; j < drawRunList.length; j++) {
        const drawSystem = drawRunList[j];
        if (!drawSystem.intraFrame || frame % drawSystem.intraFrame === 0) {
          finalRunList[frame][1].push(drawRunList[j].type);
        }
      }
    }
  }
  return finalRunList;
};
const registerPixiComponent = (type, system, zIndex = 0) => {
  if (!canRegisterComponents()) {
    throw new Error("Cannot register component after game has started");
  }
  system.prototype.__type = type;
  pixiDrawComponents.push({
    type,
    id: -1,
    system,
    zIndex
  });
};
const registerUIComponent = (type, system, options = { sort: void 0, debug: false, cleanup: void 0 }) => {
  if (!canRegisterComponents()) {
    throw new Error("Cannot register component after game has started");
  }
  uiComponents.push({
    type,
    id: -1,
    system,
    cleanup: options.cleanup,
    sort: options.sort,
    debug: options.debug
  });
};
const registerEditorComponent = (type, system) => {
  if (!canRegisterComponents()) {
    throw new Error("Cannot register component after game has started");
  }
  editorComponents[type] = {
    type,
    id: componentIndexMap[type],
    system
  };
};
const componentRegistered = (type) => {
  if (canRegisterComponents()) {
    throw new Error("Cannot check if component is registered before game has started");
  }
  return componentIndexMap[type] !== void 0;
};
const generateComponentIndexArray = (initialComponents = []) => {
  const byteArray = new ByteArray();
  for (let i = 0; i < initialComponents.length; i++) {
    byteArray.enable(componentIndexMap[initialComponents[i].type]);
  }
  return byteArray;
};
export {
  DEPTHS,
  canRegisterComponents,
  componentIndexMap,
  componentList,
  componentRegistered,
  componentsByCategory,
  editorComponents,
  generateComponentIndexArray,
  generateRunList,
  generateSystems,
  getIndex,
  pixiDrawComponents,
  registerEditorComponent,
  registerPixiComponent,
  registerSchema,
  registerSystem,
  registerUIComponent,
  systems,
  uiComponents,
  uiComponentsById
};
//# sourceMappingURL=ComponentRegistry.js.map
